import MiniSearch from 'minisearch'

// Generic MiniSearch index builder for the small "local registry" lists
// (navigation entries, external links, quick actions). Production search runs
// these registries through Fuse only; this builder exists so the internal
// search-tuning sandbox (/dev/search-tuning) can preview the same lists scored
// by MiniSearch side-by-side with Fuse. It is NOT wired into any production
// provider.
//
// Each entry is expected to have a stable `id` and a `label`. The keyword-ish
// field differs by registry (`keywords` for navigation/quick-actions, `terms`
// for external links), so both are folded into a single indexed text field.

// Mirrors DEFAULT_SEARCH_OPTIONS in buildMiniSearchIndices.js (prefix + light
// fuzzy + AND) so behaviour matches the content MiniSearch provider. `label` is
// boosted over keywords/terms to mirror the Fuse weighting. Exported so the
// search-tuning sandbox can show/reset these as the per-index MiniSearch
// defaults and let the team edit them as JSON.
export const DEFAULT_SEARCH_OPTIONS = {
  prefix: true,
  fuzzy: 0.2,
  combineWith: 'AND',
  boost: { label: 2 },
}

function processTerm(term) {
  if (typeof term !== 'string') return false
  const trimmed = term.trim().toLowerCase()
  return trimmed.length > 0 ? trimmed : false
}

/**
 * Build a fresh MiniSearch index over a registry entry array. Search-time
 * options (prefix/fuzzy/combineWith/boost) are supplied per-query in
 * searchLocalRegistryMiniSearch, so the index does not need rebuilding when
 * only those options change.
 *
 * @param {Array<Object>} entries
 * @returns {MiniSearch}
 */
export function buildLocalRegistryMiniSearch(entries) {
  const ms = new MiniSearch({
    idField: 'id',
    fields: ['label', 'keywordsText'],
    storeFields: ['label'],
    processTerm,
  })
  const docs = (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry && entry.id != null)
    .map((entry) => ({
      ...entry,
      keywordsText: [].concat(entry.keywords || [], entry.terms || []).join(' '),
    }))
  ms.addAll(docs)
  return ms
}

/**
 * Run a query against a MiniSearch registry index and return a normalised
 * result list: { id, label, score, matchedTerms }. `searchOptions` are the
 * MiniSearch search options (defaults to DEFAULT_SEARCH_OPTIONS).
 *
 * @param {MiniSearch} ms
 * @param {string} query
 * @param {Object} [searchOptions]
 * @param {number} [limit]
 * @returns {Array<Object>}
 */
export function searchLocalRegistryMiniSearch(ms, query, searchOptions, limit = 10) {
  if (!ms || !query) return []
  let results
  try {
    results = ms.search(query, searchOptions || DEFAULT_SEARCH_OPTIONS)
  } catch (e) {
    return []
  }
  const sliced = typeof limit === 'number' ? results.slice(0, limit) : results
  return sliced.map((result) => ({
    id: result.id,
    label: result.label,
    score: result.score,
    matchedTerms: result.terms,
  }))
}
